import React, { useEffect } from "react";
import { Grid, TextField, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormModal } from "@packsize/components";

import { getPickZoneConfigurationSchema, type PickZoneConfiguration } from "@/schemas";

export interface PickZoneConfigurationModalTemplateProps {
	readonly open: boolean;
	readonly defaultValues?: PickZoneConfiguration;
	readonly errorMessage?: string;
	readonly onSubmit: (data: PickZoneConfiguration) => Promise<void>;
	readonly onClose: () => void;
}

export const PickZoneConfigurationModalTemplate: React.FC<PickZoneConfigurationModalTemplateProps> = ({
	open,
	defaultValues,
	errorMessage,
	onClose,
	onSubmit,
}) => {
	const intl = useIntl();

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors, isSubmitting },
	} = useForm<PickZoneConfiguration>({
		resolver: zodResolver(getPickZoneConfigurationSchema(intl)),
		defaultValues,
	});

	useEffect(() => {
		if (open !== false) {
			return;
		}

		reset(defaultValues);
	}, [defaultValues, open, reset]);

	return (
		<FormModal
			open={open}
			title={
				defaultValues
					? intl.formatMessage({ id: "Edit Pick Zone", defaultMessage: "Edit Pick Zone" })
					: intl.formatMessage({ id: "Create Pick Zone", defaultMessage: "Create Pick Zone" })
			}
			onSubmit={handleSubmit(onSubmit)}
			size="sm"
			submitText={intl.formatMessage({ id: "Save", defaultMessage: "Save" })}
			cancelText={intl.formatMessage({ id: "Cancel", defaultMessage: "Cancel" })}
			onClose={onClose}
			onCancel={onClose}
			slotProps={{
				confirmButton: {
					loading: isSubmitting,
					"data-testid": "pick-zone-configuration-modal__button__confirm",
				},
				cancelButton: {
					"data-testid": "pick-zone-configuration-modal__button__cancel",
				},
			}}
		>
			<Grid container gap={2} direction="column">
				<TextField
					label={intl.formatMessage({ id: "Name", defaultMessage: "Name" })}
					error={Boolean(errors?.alias)}
					helperText={errors?.alias?.message}
					{...register("alias")}
					slotProps={{
						htmlInput: {
							"data-testid": "pick-zone-configuration-modal__input__alias",
						},
						formHelperText: {
							"data-testid": "pick-zone-configuration-modal__input__alias-error",
						},
					}}
				/>
				<TextField
					label={intl.formatMessage({ id: "Description", defaultMessage: "Description" })}
					error={Boolean(errors?.description)}
					helperText={errors?.description?.message}
					multiline
					rows={4}
					{...register("description")}
					slotProps={{
						htmlInput: {
							"data-testid": "pick-zone-configuration-modal__input__description",
						},
						formHelperText: {
							"data-testid": "pick-zone-configuration-modal__input__description-error",
						},
					}}
				/>

				{Boolean(errorMessage) === true && (
					<Typography color="error" whiteSpace="pre-wrap" data-testid="pick-zone-configuration-modal__save__error">
						{errorMessage}
					</Typography>
				)}
			</Grid>
		</FormModal>
	);
};
